import { detailDe } from "../diffusion-api";

/**
 * Les phrases de l'ecran « Planning » de la regie. Un code d'erreur de l'API n'a que sa phrase, ecrite ici : rien du
 * serveur n'est affiche tel quel (`diffusion-api.ts` ne le laisse pas sortir).
 */

export const PLANNING_PATH = "/regie/planning";
export const A_POSTER_PATH = "/regie/planning/a-poster";

export const NAV = {
  label: "Planning",
  semaine: "La semaine",
  aPoster: "À poster",
  regie: "Retour à la régie",
} as const;

export const CHARGEMENT = "Chargement du planning...";
export const INDISPONIBLE =
  "L'écran du planning n'a pas pu être chargé. Recharge la page : Castellan a peut-être été mis à jour depuis l'ouverture de cet onglet.";

export const SEMAINE = {
  titre: (libelle: string) => `Semaine ${libelle}`,
  precedente: "Semaine précédente",
  suivante: "Semaine suivante",
  lecture: "La semaine n'a pas pu être lue. Réessaie dans un instant.",
  relire: "Réessayer",
  chargement: "Lecture de la semaine...",
  etats: {
    brouillon: "Brouillon",
    publiee: "Publiée",
    corrigee: "Modifiée depuis sa publication",
  },
  absente: "Cette semaine n'existe pas encore.",
  absenteAide:
    "Elle sera copiée de la dernière semaine publiée avant elle, ou vide s'il n'y en a pas. Rien n'est publié.",
  preparer: "Préparer cette semaine",
  preparation: "Préparation...",
  copiee: (libelle: string) => `Copiée de la semaine ${libelle}. Change seulement ce qui bouge.`,
  vide: "Semaine saisie de zéro. Ajoute tes créneaux.",
  creneaux: "Les créneaux de la semaine",
  aucunCreneau: "Aucun créneau pour l'instant.",
  ajouter: "Ajouter un créneau",
  ajouterEvenement: "Ajouter un événement spécial",
  pleine: "Une semaine tient sept créneaux au plus.",
  evenementParDefaut: "Événement spécial",
} as const;

export const PUBLIER = {
  bouton: "Publier la semaine",
  boutonCorrigee: "Publier la nouvelle version",
  ouvrir: "Ouvrir « À poster »",
  vide: "Aucun créneau prévu : rien à publier.",
  confirmation:
    "Publier fige cette version de la semaine et fabrique tes images (story et paysage, en JPEG et en PNG). Rien n'est envoyé nulle part : tu les récupères dans « À poster ».",
  confirmer: "Publier",
  annuler: "Annuler",
  encours: "Publication...",
} as const;

export const CRENEAU = {
  groupe: (jour: string) => `Créneau de ${jour.toLowerCase()}`,
  jour: "Jour",
  debut: "Début",
  duree: "Durée",
  invite: "Invité",
  inviteAide: "Pseudo Twitch, sans @",
  off: "OFF",
  changer: "Changer",
  fermer: "Fermer",
  retirer: (jour: string) => `Retirer le créneau de ${jour.toLowerCase()}`,
  retire: "Retrait...",
  enregistrement: "Enregistrement...",
  modifie: "Changé cette semaine",
  fermee: "Pas de stream ce jour-là",
  sansVisuel: "Pas de visuel trouvé",
  envoyerVisuel: "Envoyer un visuel",
  jeuAChoisir: "Jeu à choisir",
  emissionAChoisir: "Émission à choisir",
  sousTitres: {
    jeu: "Jeu · catégorie Twitch",
    emission: "Émission",
    evenement: "Événement spécial",
  },
  inviteInvalide: "Un pseudo de 25 caractères au plus : lettres, chiffres et tiret bas.",
} as const;

export const CHOIX = {
  titre: (jour: string) => `Changer le contenu du créneau de ${jour.toLowerCase()}`,
  modes: {
    jeu: "Un jeu",
    emission: "Une émission",
    evenement: "Un événement",
    visuel: "Mon visuel",
  },
  modesLabel: "Ce que le créneau montre",
  jeu: {
    label: "Nom du jeu",
    aide: "Tape au moins deux lettres. Le visuel arrive avec le jeu.",
    recherche: "Recherche...",
    aucun: "Aucun jeu trouvé.",
    resultats: "Jeux trouvés",
    nombre: (n: number) => (n === 1 ? "1 jeu trouvé." : `${n} jeux trouvés.`),
    // Le champ posé sur chaque ligne et sur l'ajout d'un créneau.
    chercher: "Chercher un jeu",
    chercherAide: "Tape le nom : le jeu et son image se posent d'un clic.",
    chercherNeuf: "Ajouter un créneau avec un jeu",
  },
  emission: {
    label: "Émissions de la bibliothèque",
    aucune: "La bibliothèque est vide : crée ta première émission.",
    chargement: "Lecture de la bibliothèque...",
    lecture: "La bibliothèque n'a pas pu être lue.",
    nouvelle: "Nouvelle émission",
    titre: "Titre de l'émission",
    creer: "Ajouter à la bibliothèque",
    creation: "Ajout...",
  },
  evenement: {
    titre: "Titre de l'événement",
    utiliser: "Utiliser cet événement",
  },
  visuel: {
    label: "Image du créneau",
    aide: "Un JPEG ou un PNG. Il est réduit avant l'envoi.",
    reduction: "Réduction de l'image...",
    envoi: "Envoi du visuel...",
    pret: "Visuel prêt.",
    retirer: "Retirer le visuel",
  },
} as const;

export const APERCU = {
  label: "Aperçu de l'affiche",
  format: "Format de l'aperçu",
  story: "Story",
  paysage: "Paysage",
  image: (format: string) => `Aperçu de l'affiche de la semaine au format ${format}`,
  suit: "L'aperçu suit la saisie, créneau par créneau.",
  vide: "Ajoute un créneau pour voir l'affiche.",
} as const;

export const KIT = {
  titre: "À poster",
  semaine: (libelle: string) => `Semaine ${libelle}`,
  chargement: "Lecture du kit...",
  lecture: "Le kit n'a pas pu être lu. Réessaie dans un instant.",
  jamais: "Cette semaine n'a jamais été publiée.",
  versSemaine: "Aller à « La semaine »",
  version: (n: number) => `Version ${n}`,
  fichiers: "Les images",
  texte: "Le texte à coller",
  copier: "Copier",
  copie: "Copié.",
  copieEchec: "La copie n'a pas marché : sélectionne le texte à la main.",
  telecharger: "Télécharger",
  muet: "Le service de rendu ne répond pas : tes images attendront son retour.",
  direct: "Un direct est en cours : la file des images attend sa fin.",
  etats: {
    en_attente: "en file",
    en_cours: "en cours",
    fait: "prêt",
    echoue: "en échec",
  },
  formats: { story: "Story", paysage: "Paysage" },
  sorties: { png: "PNG", jpeg: "JPEG" },
  fichier: (format: string, sortie: string) => `${format} en ${sortie}`,
} as const;

export const SESSION = {
  refusee: "Ce compte n'a pas accès à la régie.",
} as const;

/** La phrase d'un refus d'ecriture ou de lecture du planning : le code de l'API, ou le statut, jamais son message. */
export function erreurText(erreur: unknown): string {
  const { code, status } = detailDe(erreur);
  switch (code) {
    case "delai_depasse":
    case "coupure":
    case "contract_broken":
      return "La demande a peut-être abouti : la semaine est relue pour le savoir.";
    case "semaine_pleine":
      return SEMAINE.pleine;
    case "semaine_vide":
      return PUBLIER.vide;
    case "semaine_existe":
      return "Cette semaine existe déjà : elle est relue.";
    case "reference_inconnue":
      return "Cette émission ou ce visuel n'existe plus. Choisis-en un autre.";
    case "image_trop_grande":
    case "corps_trop_lourd":
      return "Cette image est trop grande. Choisis-en une plus petite.";
    case "ni_jpeg_ni_png":
      return "Ce fichier n'est ni un JPEG ni un PNG.";
    case "twitch_non_relie":
      return "La chaîne n'est pas reliée à Twitch : la recherche d'un jeu attend.";
    case "twitch_refuse":
      return "Twitch n'a pas répondu. Réessaie dans un instant.";
    case "base_indisponible":
      return "La base n'a pas répondu. Réessaie dans un instant.";
    case "jamais_publiee":
      return KIT.jamais;
    default:
      break;
  }
  if (status === 404) return "Cette ressource n'existe plus : la semaine est relue.";
  if (status === 429) return "Trop de demandes d'un coup. Réessaie dans un instant.";
  return "La demande a échoué. Réessaie dans un instant.";
}

export const IMAGE_ERREURS = {
  trop_lourde: "Ce fichier pèse plus de 30 Mo : choisis une image plus légère.",
  illisible: "Ce fichier n'est pas une image que le navigateur sait lire.",
  trop_grosse_apres_reduction: "Cette image reste trop lourde même réduite : choisis-en une autre.",
} as const;
